import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { MESSAGES } from "../../messages";
import {
  EngagementRecord,
  StewardshipFilterParam,
} from "../../models/stewardshipModels";
import { APIClient } from "../../services/api/ApiClient";
import { API_ROUTES } from "../../services/api/config";
import { useApp } from "../../store/AppProvider";
import { useToast } from "../../store/ToastProvider";
import {
  DashboardFilters,
  EngagementAssessmentV2HistoryResponse,
  GetEngagementAssessmentV2Request,
  LatestRunPerEngagement,
} from "../../types/engagementAssessmentV2Types";
import { usePost } from "../../hooks/usePost";
import { latestRunPerEngagement } from "./aggregations";

export const DEFAULT_HISTORY_FETCH_CONCURRENCY = 8;

type ApiEnvelope<T> = { status: string; message: string; data?: T };

export type UseAssessmentInsightsDataReturn = {
  latestRuns: LatestRunPerEngagement[];
  isLoading: boolean;
  error: string | null;
  isEmpty: boolean;
  refresh: () => void;
};

const runChunked = async <TItem, TResult>(
  items: TItem[],
  task: (item: TItem) => Promise<TResult>,
  concurrency: number,
): Promise<TResult[]> => {
  const size = Math.max(1, concurrency);
  const out: TResult[] = [];
  for (let i = 0; i < items.length; i += size) {
    const chunk = items.slice(i, i + size);
    // eslint-disable-next-line no-await-in-loop
    const results = await Promise.all(chunk.map(task));
    out.push(...results);
  }
  return out;
};

const themesForEngagement = (record: EngagementRecord): string[] => {
  const themes: string[] = [];
  if (record.theme) themes.push(record.theme);
  if (record.meta_theme && !themes.includes(record.meta_theme)) {
    themes.push(record.meta_theme);
  }
  return themes;
};

export const useAssessmentInsightsData = (
  filters: DashboardFilters,
  concurrency: number = DEFAULT_HISTORY_FETCH_CONCURRENCY,
): UseAssessmentInsightsDataReturn => {
  const { app } = useApp();
  const { addToast } = useToast();

  const [latestRuns, setLatestRuns] = useState<LatestRunPerEngagement[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState<number>(0);

  const { sendData: fetchEngagementList } = usePost<
    EngagementRecord[],
    StewardshipFilterParam
  >();

  const addToastRef = useRef(addToast);
  addToastRef.current = addToast;
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const fetchEngagementListRef = useRef(fetchEngagementList);
  fetchEngagementListRef.current = fetchEngagementList;
  const tokenRef = useRef(app.authorisation.token);
  tokenRef.current = app.authorisation.token;

  const cycleIdRef = useRef<number>(0);

  const managerKey = useMemo(
    () => [...filters.managerIds].sort().join(","),
    [filters.managerIds],
  );
  const themeKey = useMemo(
    () => [...filters.themes].sort().join(","),
    [filters.themes],
  );

  useEffect(() => {
    cycleIdRef.current += 1;
    const myCycle = cycleIdRef.current;

    const run = async (): Promise<void> => {
      setIsLoading(true);
      setError(null);

      const currentFilters = filtersRef.current;

      const listPayload: StewardshipFilterParam = {};
      if (currentFilters.managerIds.length > 0) {
        listPayload.manager_id = currentFilters.managerIds;
      }

      const engagements = await fetchEngagementListRef.current(
        "GET_ENGAGEMENT_DATA",
        listPayload,
      );

      if (cycleIdRef.current !== myCycle) return;

      if (engagements === null) {
        const msg =
          MESSAGES.ENGAGEMENT_ASSESSMENT_V2.ERRORS.HISTORY_FETCH_FAILED;
        setError(msg);
        addToastRef.current(msg, "error");
        setLatestRuns([]);
        setIsLoading(false);
        return;
      }

      const themeSet =
        currentFilters.themes.length > 0
          ? new Set(currentFilters.themes)
          : null;
      const filteredEngagements =
        themeSet === null
          ? engagements
          : engagements.filter((eng) =>
              themesForEngagement(eng).some((t) => themeSet.has(t)),
            );

      if (filteredEngagements.length === 0) {
        setLatestRuns([]);
        setIsLoading(false);
        return;
      }

      const apiClient = new APIClient();
      const requestConfig = {
        headers: {
          token: tokenRef.current,
          "Content-Type": "application/json",
        },
      };

      let hadFetchFailure = false;

      const fetchOne = async (
        record: EngagementRecord,
      ): Promise<LatestRunPerEngagement | null> => {
        try {
          const body: GetEngagementAssessmentV2Request = {
            engagement_record_id: record.engagement_record_id,
          };
          const envelope = await apiClient.post<
            ApiEnvelope<EngagementAssessmentV2HistoryResponse>,
            GetEngagementAssessmentV2Request
          >(API_ROUTES.AI_GET_ENGAGEMENT_ASSESSMENT_V2, body, requestConfig);
          const runs = envelope?.data?.runs ?? [];
          const latest = latestRunPerEngagement(runs);
          if (latest === null) return null;
          return {
            engagement_record_id: record.engagement_record_id,
            manager_id: record.manager_id,
            manager_name: record.manager_name,
            themes: themesForEngagement(record),
            assessed_at: latest.assessed_at,
            assessment: latest.assessment,
          };
        } catch {
          hadFetchFailure = true;
          return null;
        }
      };

      const perEngagement = await runChunked(
        filteredEngagements,
        fetchOne,
        concurrency,
      );

      if (cycleIdRef.current !== myCycle) return;

      const rows = perEngagement.filter(
        (r): r is LatestRunPerEngagement => r !== null,
      );

      if (hadFetchFailure) {
        const msg =
          MESSAGES.ENGAGEMENT_ASSESSMENT_V2.ERRORS.HISTORY_FETCH_FAILED;
        setError(msg);
        addToastRef.current(msg, "error");
      }

      setLatestRuns(rows);
      setIsLoading(false);
    };

    void run();
  }, [managerKey, themeKey, version, concurrency]);

  const refresh = useCallback((): void => {
    setVersion((v) => v + 1);
  }, []);

  const isEmpty = !isLoading && latestRuns.length === 0;

  return { latestRuns, isLoading, error, isEmpty, refresh };
};
